import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { CTAButton } from "@/components/CTAButton";
import EnquiryForm from "@/components/EnquiryForm";
import { SERVICES, CONTACT } from "@/data/content";

export default function ServiceDetail({ slug }) {
  const service = SERVICES.find((s) => s.slug === slug);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 100]);
  if (!service) return null;

  return (
    <div>
      {/* Full-bleed textural hero */}
      <section className="relative h-[85vh] min-h-[560px] overflow-hidden" data-testid="service-hero">
        <motion.img style={{ y }} src={service.hero} alt={service.title} className="absolute inset-0 w-full h-[115%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/50" />
        <div className="relative h-full container-x flex flex-col justify-end pb-16 md:pb-24 text-white">
          <Overline light className="mb-6">Specialist Discipline · {service.index}</Overline>
          <h1 className="font-display text-6xl md:text-8xl lg:text-[8rem] font-extrabold leading-[0.85] tracking-tighter uppercase">{service.title}</h1>
          <p className="mt-6 text-[0.8rem] uppercase tracking-[0.25em] text-sand">{service.tagline}</p>
        </div>
      </section>

      {/* Intro + specs */}
      <section className="container-x py-24 md:py-32 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-display text-2xl md:text-3xl font-bold leading-snug text-charcoal">{service.short}</p>
            <p className="mt-8 text-lg font-light text-taupe leading-relaxed">{service.intro}</p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="border-t border-charcoal">
              {service.specs.map((sp) => (
                <div key={sp.label} className="flex justify-between items-center py-4 border-b border-black/10">
                  <span className="eyebrow">{sp.label}</span>
                  <span className="font-mono text-sm text-charcoal">{sp.value}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-offwhite py-24 md:py-32" data-testid="service-benefits">
        <div className="container-x">
          <Overline className="mb-14">Why It Matters</Overline>
          <div className="grid md:grid-cols-3 gap-px bg-black/10">
            {service.benefits.map((b, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-offwhite h-full p-8 md:p-10">
                  <span className="font-mono text-sm text-sand">0{i + 1}</span>
                  <h3 className="font-display text-2xl font-bold mt-4 mb-3">{b.title}</h3>
                  <p className="text-taupe font-light leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Applications + gallery */}
      <section className="container-x py-24 md:py-32 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-4">
          <Overline className="mb-8">Key Applications</Overline>
          <ul className="space-y-4">
            {service.applications.map((a) => (
              <li key={a} className="flex items-center gap-4 border-b border-black/10 pb-4">
                <Check className="h-5 w-5 text-sand shrink-0" strokeWidth={1.5} />
                <span className="text-lg font-light">{a}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-8 grid grid-cols-2 gap-4">
          {service.gallery.map((img, i) => (
            <Reveal key={i} delay={(i % 2) * 0.1}>
              <div className={`relative overflow-hidden ${i % 3 === 0 ? "aspect-[4/5]" : "aspect-square"}`}>
                <img src={img} alt={`${service.title} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[800ms]" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Enquire CTA */}
      <section className="bg-charcoal text-white py-24 md:py-32" data-testid="service-enquire">
        <div className="container-x grid lg:grid-cols-2 gap-16">
          <div>
            <Overline light className="mb-6">Enquire Now</Overline>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold leading-tight mb-8">Considering {service.title.toLowerCase()} for your project?</h2>
            <p className="text-white/70 font-light leading-relaxed max-w-md mb-8">Tell us about your space and we'll return an obligation-free quote within one business day.</p>
            <a href={CONTACT.phoneHref} className="font-display text-3xl font-bold hover:text-sand transition-colors">{CONTACT.phone}</a>
          </div>
          <div className="bg-white p-8 md:p-10">
            <EnquiryForm defaultService={service.title} />
          </div>
        </div>
      </section>
    </div>
  );
}
